import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import styled from "styled-components";
import type {
  CollectionHierarchyQuery,
  CollectionNode,
} from "src/types/collection/collectionTypes";
import { useCollectionStore } from "src/store/collection/collectionStore";
import {
  buildVisibleRows,
  selectCollectionError,
  selectCollectionLoading,
  selectCollectionPageData,
  selectCollectionQuery,
  selectExpandedIds,
} from "src/store/collection/collectionSelectors";
import CollectionUpsertModal from "src/components/collection/CollectionUpsertModal";
import {
  ManageBtns,
  TableWrap,
  StyledTable,
  EmptyBox,
  LoadingBox,
} from "src/components/common/TableButtons";
import { Pagination, Stack, Switch } from "@mui/material";
import CustomButton from "src/components/common/CustomButton";
import { useProductStore } from "src/store/product/product.store";

export default function CollectionManagePage() {
  const navigate = useNavigate();
  const query = useCollectionStore(selectCollectionQuery);
  const pageData = useCollectionStore(selectCollectionPageData);
  const loading = useCollectionStore(selectCollectionLoading);
  const error = useCollectionStore(selectCollectionError);
  const expandedIds = useCollectionStore(selectExpandedIds);

  const setQuery = useCollectionStore((s) => s.setQuery);
  const fetchHierarchy = useCollectionStore((s) => s.fetchHierarchy);
  const toggleExpand = useCollectionStore((s) => s.toggleExpand);
  const expandAllVisible = useCollectionStore((s) => s.expandAllVisible);
  const collapseAll = useCollectionStore((s) => s.collapseAll);
  const deleteCollection = useCollectionStore((s) => s.deleteCollection);
  const toggleCollectionActive = useCollectionStore((s) => s.toggleCollectionActive);

  const setProductQuery = useProductStore((s) => s.setQuery);

  /** ===== 모달 상태 ===== */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<CollectionNode | null>(null);

  useEffect(() => {
    fetchHierarchy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.size, query.order]);

  const rows = useMemo(() => {
    const roots = pageData?.content || [];
    return buildVisibleRows(roots, expandedIds);
  }, [pageData, expandedIds]);

  const onChangeQuery = (patch: Partial<CollectionHierarchyQuery>) => {
    setQuery({ ...patch, page: 0 });
  };

  const handleProductCountClick = (collectionId: number) => {
    setProductQuery({ collectionId: String(collectionId), page: 1 });
    navigate("/product");
  };

  return (
    <Wrap>
      <Header>
        <Title>컬렉션 관리</Title>
        <Actions>
          <CustomButton onClick={expandAllVisible}>전체 펼치기</CustomButton>
          <CustomButton onClick={collapseAll}>전체 접기</CustomButton>
          <CustomButton
            variant="contained"
            onClick={() => {
              setModalMode("create");
              setEditing(null);
              setModalOpen(true);
            }}
          >
            컬렉션 생성
          </CustomButton>
        </Actions>
      </Header>

      {/* ===== 검색 필터 ===== */}
      <FilterBar>
        <Field>
          <Input
            value={query.collectionName || ""}
            onChange={(e) => onChangeQuery({ collectionName: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchHierarchy();
              }
            }}
            placeholder="예: 봄 신상품"
          />
        </Field>

        {/* <Field>
          <Label>정렬</Label>
          <Select
            value={query.order || "displayOrder,asc"}
            onChange={(e) => setQuery({ order: e.target.value })}
          >
            <option value="displayOrder,asc">진열순(오름차순)</option>
            <option value="displayOrder,desc">진열순(내림차순)</option>
            <option value="createdAt,desc">생성일(최신)</option>
          </Select>
        </Field> */}

        <CustomButton variant="contained" onClick={fetchHierarchy} disabled={loading}>
          검색
        </CustomButton>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* ===== 테이블 ===== */}
      <TableWrap>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: 250 }}>컬렉션명</th>
              <th style={{ width: 90 }}>컬렉션코드</th>
              <th style={{ width: 90 }}>컬렉션ID</th>
              <th style={{ width: 80 }}>상품수</th>
              <th style={{ width: 110 }}>활성</th>
              <th style={{ width: 80 }}>노출기간</th>
              <th style={{ width: 160 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7}>
                  <LoadingBox>불러오는 중...</LoadingBox>
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyBox>데이터가 없습니다.</EmptyBox>
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((r) => (
                <tr key={r.node.collectionId}>
                  <td>
                    <NameCell>
                      <Indent style={{ width: r.level * 18 }} />
                      {r.hasChildren ? (
                        <ExpandBtn type="button" onClick={() => toggleExpand(r.node.collectionId)}>
                          {r.isExpanded ? "▾" : "▸"}
                        </ExpandBtn>
                      ) : (
                        <ExpandPlaceholder />
                      )}
                      <NameText title={r.node.collectionName}>{r.node.collectionName}</NameText>
                      <ChildCount>{r.hasChildren ? `(${r.childrenCount})` : ""}</ChildCount>
                    </NameCell>
                  </td>
                  <td>{r.node.collectionCode}</td>
                  <td>{r.node.collectionId}</td>
                  <td>
                    <ProductCount onClick={() => handleProductCountClick(r.node.collectionId)}>
                      {r.node.productCount ?? 0}
                    </ProductCount>
                  </td>
                  <td>
                    <Switch
                      checked={r.node.isActive}
                      size="small"
                      disabled={loading}
                      onChange={() => toggleCollectionActive(r.node)}
                    />
                  </td>
                  <td>
                    {fmtDate(r.node.displayStartDate ?? "")} ~{" "}
                    {fmtDate(r.node.displayEndDate ?? "")}
                  </td>
                  <td>
                    <ManageBtns>
                      <CustomButton
                        onClick={() => {
                          setModalMode("edit");
                          setEditing(r.node);
                          setModalOpen(true);
                        }}
                      >
                        수정
                      </CustomButton>
                      <CustomButton
                        customVariant="danger"
                        onClick={async () => {
                          const result = await Swal.fire({
                            title: "삭제 확인",
                            text: "삭제할까요?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "삭제",
                            cancelButtonText: "취소",
                            didOpen: () => {
                              const container = Swal.getContainer();
                              if (container) {
                                container.style.zIndex = "2000";
                              }
                            },
                          });
                          if (result.isConfirmed) {
                            deleteCollection(r.node.collectionId);
                          }
                        }}
                      >
                        삭제
                      </CustomButton>
                    </ManageBtns>
                  </td>
                </tr>
              ))}
          </tbody>
        </StyledTable>
      </TableWrap>

      <Stack mt={2} direction="row" spacing={2} alignItems="center" justifyContent="center">
        <Pagination
          page={(pageData?.page ?? 0) + 1} // ✅ MUI = 1-based
          count={Math.max(1, pageData?.totalPages ?? 1)}
          disabled={loading || !pageData}
          showFirstButton
          showLastButton
          onChange={(_: React.ChangeEvent<unknown>, page: number) => {
            setQuery({ page: page - 1 }); // ✅ 상태는 0-based
          }}
        />
      </Stack>

      {/* ===== 생성/수정 모달 ===== */}
      <CollectionUpsertModal
        open={modalOpen}
        mode={modalMode}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />
    </Wrap>
  );
}

/* ===== util ===== */
function fmtDate(iso?: string) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function pad2(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

/* ===== styles ===== */
/* (네가 올린 스타일 그대로라 생략 안 하고 유지) */

const Wrap = styled.div`
  padding: 20px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 250px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Indent = styled.div`
  flex: 0 0 auto;
`;

const ExpandBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
`;

const ExpandPlaceholder = styled.div`
  width: 26px;
  height: 26px;
`;

const NameText = styled.div`
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChildCount = styled.div`
  font-size: 12px;
  color: #888;
`;

const ErrorBox = styled.div`
  padding: 10px 12px;
  border: 1px solid #ffd7d7;
  background: #fff3f3;
  color: #b00000;
  border-radius: 10px;
  margin-bottom: 12px;
`;

const ProductCount = styled.span`
  color: #1890ff;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
